import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";

import { ContractContext } from "../../../../providers/contract";
import {
  AddOnFeeCategory,
  ESenderRole,
  ETrasactionsType,
  IShipmentDTO,
  TransactionStatus,
} from "../../../../setup/api/services/contract/models";
import { makePaymentLinkCashout } from "../../../../setup/api/services/payment";
import { IPaymentLinkCashoutAttachment } from "../../../../setup/api/services/payment/models";
import { uploadFileWithPresignedUrl } from "../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../s3Integration/enums";
import { handleCompressImage } from "../../../../setup/util/helpers";
import Modal from "../../../../components/common/Modal/Modal";
import ImageUpload from "../../../../components/forms/imageUpload/ImageUpload";
import PDFComponent from "../../../../components/common/PDFComponent/PDFComponent";
import PrimaryButton from "../../../../components/common/PrimaryButton/PrimaryButton";
import OutlinedButton from "../../../../components/common/OutlinedButton/OutlinedButton";
import Spinner from "../../../../components/common/Spinner";
import TextInput from "../../../../components/forms/textInput/textInput";
import styles from "./styles.module.scss";

interface ILockerShipmentCardProps {
  shipment: IShipmentDTO;
}

const MAX_ATTACHMENTS = 3;

interface IAttachmentItem {
  id: string;
  file: File;
  previewUrl: string;
  uploaded?: IPaymentLinkCashoutAttachment;
  uploading: boolean;
  loadingProgress: number;
  error: boolean;
}

const LockerShipmentCard = ({ shipment }: ILockerShipmentCardProps) => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [gatewayPaymentId, setGatewayPaymentId] = useState("");
  const [attachments, setAttachments] = useState<IAttachmentItem[]>([]);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const brokerParty = contract?.parties?.find(
    (p) => p.role === ESenderRole.BROKER && !p.arbitrator
  );

  const isCashedOut = contract?.transactions?.some(
    (txn) =>
      txn.type === ETrasactionsType.CASH_OUT &&
      txn.status === TransactionStatus.SUCCESS &&
      txn.beneficiaryId === brokerParty?.userId
  );

  const openModal = () => {
    attachments.forEach((a) => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    });
    setGatewayPaymentId("");
    setAttachments([]);
    setLoadedImages({});
    setIsSuccess(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const handleSuccessClose = () => {
    setModalOpen(false);
    window.location.reload();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!contract) return;

    const selectedFiles = Array.from(e.target.files || []).slice(
      0,
      MAX_ATTACHMENTS - attachments.length
    );

    const tempItems: IAttachmentItem[] = [];

    for (const file of selectedFiles) {
      let uploadTarget = file;
      if (file.type !== "application/pdf") {
        try {
          const compressed = await handleCompressImage(file);
          if (compressed) uploadTarget = compressed;
        } catch {
          // use original if compression fails
        }
      }

      tempItems.push({
        id: Math.random().toString(),
        file: uploadTarget,
        previewUrl: URL.createObjectURL(uploadTarget),
        uploading: true,
        loadingProgress: 0,
        error: false,
      });
    }

    setAttachments((prev) => [...prev, ...tempItems]);

    for (const item of tempItems) {
      try {
        const result = await uploadFileWithPresignedUrl(
          item.file,
          EUploadImageType.CONTRACT,
          contract.id,
          (progress) => {
            setAttachments((prev) =>
              prev.map((a) =>
                a.id === item.id
                  ? {
                      ...a,
                      loadingProgress: Math.round(
                        (progress.loaded / progress.total) * 100
                      ),
                    }
                  : a
              )
            );
          }
        );
        const uploaded: IPaymentLinkCashoutAttachment = {
          url: result.url,
          bucketName: result.bucket,
          objectName: result.key,
          isPublic: false,
        };
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === item.id
              ? { ...a, uploading: false, loadingProgress: 0, uploaded }
              : a
          )
        );
      } catch {
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === item.id ? { ...a, uploading: false, error: true } : a
          )
        );
      }
    }
  };

  const handleDelete = (id: string) => {
    setAttachments((prev) => {
      const item = prev.find((a) => a.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleConfirm = async () => {
    if (!contract) return;

    const refTxn = contract.transactions?.find(
      (txn) =>
        txn.status === TransactionStatus.SUCCESS &&
        (txn.type === "CUSTOMER_PAY" || txn.type === "MANUAL_BANK_TRANSFER")
    );

    const brokerProfile = contract.partiesProfiles?.[ESenderRole.BROKER]?.find(
      (p) => String(p.userIdentification) === brokerParty?.userId
    );

    const shippingFee = contract.addonFeesList?.find(
      (f) => f.type === AddOnFeeCategory.SHIPPING_FEES
    );

    if (!refTxn || !brokerParty?.userId || !shippingFee) {
      toast.error(t("lbl_locker_cashout_missing_data"));
      return;
    }

    const uploadedAttachments = attachments
      .filter((a) => a.uploaded)
      .map((a) => a.uploaded as IPaymentLinkCashoutAttachment);

    setSubmitting(true);
    try {
      await makePaymentLinkCashout({
        referenceTransactionId: refTxn.referenceTransactionId,
        assignedBeneficiaryId: brokerParty.userId,
        assignedBeneficiaryName: brokerProfile?.profile?.fullName ?? undefined,
        amount: shippingFee.amount,
        gatewayPaymentId: gatewayPaymentId.trim() || undefined,
        paymentLink: shipment.lockerPaymentUrl ?? undefined,
        attachments:
          uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
      });
      setIsSuccess(true);
    } catch {
      toast.error(t("lbl_locker_cashout_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const anyUploading = attachments.some((a) => a.uploading);
  const fee = shipment.fee || 0;
  const isPaid = shipment.isLockerShipmentPaid === true;

  const isPdf = (file: File) =>
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

  return (
    <div className={styles.container}>
      {isPaid ? (
        <>
          {/* Paid state */}
          <div className={styles.paid_header}>
            <div className={styles.paid_title_group}>
              <p className={styles.title}>
                {t(
                  isCashedOut
                    ? "lbl_locker_redbox_transferred_title"
                    : "lbl_locker_redbox_paid_title"
                )}
              </p>
              <p className={styles.subtitle}>
                {t(
                  isCashedOut
                    ? "lbl_locker_redbox_transferred_subtitle"
                    : "lbl_locker_redbox_paid_subtitle"
                )}
              </p>
            </div>
            <div className={styles.done_icon}>
              <svg
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4 10L8 14L16 6"
                  stroke="#ffffff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className={styles.detail_rows_container}>
            <div className={styles.detail_row_paid}>
              <p className={styles.detail_value}>
                {fee.toFixed(2)} {t("lbl_saudi_currency")}
              </p>
              <p className={styles.detail_label}>
                {t("lbl_locker_redbox_amount_paid")}
              </p>
            </div>

            {shipment.lockerCashoutGatewayPaymentId != null && (
              <>
                <div className={styles.detail_divider} />
                <div className={styles.detail_row_paid}>
                  <p className={styles.detail_value}>
                    {shipment.lockerCashoutGatewayPaymentId || "-"}
                  </p>
                  <p className={styles.detail_label}>
                    {t("lbl_locker_redbox_reference")}
                  </p>
                </div>
              </>
            )}

            {shipment.lockerCashoutAttachmentsViewUrl && (
              <>
                <div className={styles.detail_divider} />
                <div className={styles.detail_row_paid}>
                  <a
                    href={shipment.lockerCashoutAttachmentsViewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.detail_link}>
                    {t("lbl_locker_redbox_view_attachments")}
                  </a>
                  <p className={styles.detail_label}>{t("lbl_attachments")}</p>
                </div>
              </>
            )}

            {shipment.lockerCashoutRegisteredAt && (
              <>
                <div className={styles.detail_divider} />
                <div className={styles.detail_row_paid}>
                  <p className={styles.detail_value}>
                    {moment(shipment.lockerCashoutRegisteredAt).format(
                      "D MMMM YYYY - hh:mm A"
                    )}
                  </p>
                  <p className={styles.detail_label}>
                    {t("lbl_locker_redbox_payment_date")}
                  </p>
                </div>
              </>
            )}
          </div>

          {!isCashedOut && (
            <div className={styles.buttons_row}>
              <OutlinedButton onClick={openModal} disabled={submitting}>
                {t("lbl_register_payment")}
              </OutlinedButton>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Unpaid state */}
          <div className={styles.header}>
            <img
              src="/assets/images/svg/lockerPayment.svg"
              alt=""
              className={styles.icon}
            />
            <div className={styles.title_group}>
              <p className={styles.title}>
                {t("lbl_locker_redbox_fees_title")}
              </p>
              <p className={styles.subtitle}>
                {t("lbl_locker_redbox_fees_subtitle")}
              </p>
            </div>
          </div>

          <div className={styles.fee_row}>
            <p className={styles.fee_amount}>
              {fee.toFixed(2)} {t("lbl_saudi_currency")}
            </p>
            <p className={styles.fee_label}>{t("lbl_locker_fees_label")}</p>
          </div>

          <div className={styles.buttons_row}>
            {shipment.lockerPaymentUrl ? (
              <a
                href={shipment.lockerPaymentUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.pay_link_btn}>
                {t("lbl_pay_fees")}
              </a>
            ) : (
              <button className={styles.pay_link_btn} disabled>
                {t("lbl_pay_fees")}
              </button>
            )}
          </div>
        </>
      )}

      {/* Register-payment modal */}
      <Modal visible={modalOpen} onCancel={closeModal} size="large">
        <div className={styles.modal_container}>
          {isSuccess ? (
            /* Success state */
            <div className={styles.success_container}>
              <img
                src="/assets/images/svg/wallet_withdraw_success.svg"
                alt=""
                className={styles.success_icon}
              />
              <h3 className={styles.success_title}>
                {t("lbl_locker_redbox_success_title")}
              </h3>
              <p className={styles.success_subtitle}>
                {t("lbl_locker_redbox_success_subtitle")}
              </p>
              <button
                className={styles.success_btn}
                onClick={handleSuccessClose}>
                {t("lbl_locker_redbox_success_btn")}
              </button>
            </div>
          ) : (
            /* Form state */
            <>
              <h3 className={styles.modal_title}>
                {t("lbl_locker_redbox_confirm_modal_title")}
              </h3>
              <p className={styles.modal_subtitle}>
                {t("lbl_locker_redbox_confirm_modal_subtitle")}
              </p>

              <div className={styles.field}>
                <label className={styles.field_label}>
                  {t("lbl_gateway_payment_id")}
                </label>
                <TextInput
                  value={gatewayPaymentId}
                  handleChange={(e) => setGatewayPaymentId(e.target.value)}
                  placeholder={t("lbl_gateway_payment_id_placeholder")}
                  disabled={submitting}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.field_label}>
                  {t("lbl_locker_redbox_attachments_label")}
                </label>

                <div className={styles.images_upload_section}>
                  {attachments.length < MAX_ATTACHMENTS && (
                    <ImageUpload
                      multiple
                      withPDF
                      disabled={submitting}
                      handleChange={handleUpload}
                    />
                  )}

                  {attachments.map((a) => (
                    <div key={a.id} className={styles.uploaded_container}>
                      {a.uploading ? (
                        <div className={styles.upload_wrapper}>
                          {a.file.type?.startsWith("image/") && a.previewUrl ? (
                            <img
                              src={a.previewUrl}
                              alt="preview"
                              className={styles.local_image}
                            />
                          ) : (
                            <PDFComponent fileUrl="" isPreview />
                          )}
                          <div
                            style={{ height: `${100 - a.loadingProgress}%` }}
                            className={styles.upload_overlay}
                          />
                          <div className={styles.upload_overlay_text}>
                            <span>{a.loadingProgress}%</span>
                          </div>
                        </div>
                      ) : a.error ? (
                        <div className={styles.upload_error_box}>✕</div>
                      ) : isPdf(a.file) ? (
                        <PDFComponent
                          fileUrl={a.uploaded?.url || a.previewUrl}
                          isPreview
                        />
                      ) : (
                        <img
                          className={`${styles.uploaded_image} ${
                            loadedImages[a.id] ? styles.loaded : ""
                          }`}
                          src={a.previewUrl}
                          alt="Uploaded"
                          onLoad={() =>
                            setLoadedImages((prev) => ({
                              ...prev,
                              [a.id]: true,
                            }))
                          }
                        />
                      )}

                      <div onClick={() => handleDelete(a.id)}>
                        <img
                          className={styles.delete_icon}
                          src="/assets/images/svg/delete_org.svg"
                          alt="delete"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.button_container}>
                <PrimaryButton
                  onClick={handleConfirm}
                  disabled={submitting || anyUploading}>
                  {submitting ? (
                    <Spinner />
                  ) : (
                    t("lbl_locker_redbox_confirm_payment")
                  )}
                </PrimaryButton>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LockerShipmentCard;
